import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { AnimatePresence } from 'framer-motion';
import Notification from '../components/Notification';

const NotificationContext = createContext(null);

/**
 * Proveedor global de notificaciones.
 * Envuelve la app y renderiza el stack de toasts en el tope del DOM.
 *
 * IMPORTANTE: también sobreescribe window.alert() para que todos los alert()
 * del sistema pasen por el toast en lugar del popup nativo del navegador.
 *
 * Uso desde cualquier componente:
 *   const { showNotification } = useNotification();
 *   showNotification('Guardado exitosamente', 'success');
 *   showNotification(parseErrorMessage(error), 'error');
 */
export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const showNotificationRef = useRef(null);

  const showNotification = useCallback((message, type = 'info', duration = 5000) => {
    const id = Date.now() + Math.random();
    setNotifications(prev => [...prev, { id, message, type, duration }]);
  }, []);

  // Mantener ref actualizada para usarla en el override de window.alert
  showNotificationRef.current = showNotification;

  // Sobreescribir window.alert para capturar todos los alert() del sistema
  useEffect(() => {
    const originalAlert = window.alert;

    window.alert = (message) => {
      if (!message) return;
      const msg = String(message);
      // Detectar tipo por palabras clave en el mensaje
      let type = 'info';
      if (/no se pud[oi]|error|falló|fallo|verificá|intentá|problema/i.test(msg)) {
        type = 'error';
      } else if (/exitosamente|guardado|creado|actualizado|eliminado|completado|listo|ok\b/i.test(msg)) {
        type = 'success';
      } else if (/ingresá|seleccioná|requerido|obligatorio|mínimo|máximo|debe|advertencia/i.test(msg)) {
        type = 'warning';
      }
      // Preservar saltos de línea reales; solo limpiar \n escapados literales
      const cleanMsg = msg.replace(/\\n/g, '\n').replace(/\t/g, ' ').trim();
      showNotificationRef.current(cleanMsg, type, type === 'error' ? 8000 : 5000);
    };

    return () => {
      window.alert = originalAlert;
    };
  }, []);

  const removeNotification = useCallback((id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  return (
    <NotificationContext.Provider value={{ showNotification }}>
      {children}

      {/* Stack de toasts — renderizado fuera del flujo normal para evitar z-index issues */}
      <div className="fixed top-4 left-1/2 z-[9999] flex flex-col gap-2 pointer-events-none"
           style={{ transform: 'translateX(-50%)', minWidth: 300, maxWidth: 520 }}>
        <AnimatePresence>
          {notifications.map(n => (
            <div key={n.id} className="pointer-events-auto">
              <Notification
                message={n.message}
                type={n.type}
                duration={n.duration}
                onClose={() => removeNotification(n.id)}
              />
            </div>
          ))}
        </AnimatePresence>
      </div>
    </NotificationContext.Provider>
  );
};

/**
 * Hook para mostrar notificaciones desde cualquier componente.
 * Requiere que el componente esté dentro de <NotificationProvider>.
 */
export const useNotification = () => {
  const ctx = useContext(NotificationContext);
  if (!ctx) {
    // Fallback silencioso — no rompe si se usa fuera del provider
    return { showNotification: (msg, type) => console.warn('[Notification]', type, msg) };
  }
  return ctx;
};
